export type PaymentAnalyticsRecord = {
  paymentId: number
  providerPaymentId?: string
  amountMinor?: number
  currency?: string
  credits?: number
  packCode?: string
  packTitle?: string
  createdAtMs: number
}

export const PAYMENT_ANALYTICS_OUTBOX_KEY = 'billing.payment_analytics_outbox_v1'
export const PAYMENT_ANALYTICS_OUTBOX_UPDATED_EVENT = 'billing:payment-analytics-outbox-updated'

const MAX_RECORDS = 20
const MAX_RECORD_AGE_MS = 7 * 24 * 60 * 60 * 1000

function normalizeRecord(raw: unknown): PaymentAnalyticsRecord | null {
  if (!raw || typeof raw !== 'object') return null
  const value = raw as Record<string, unknown>
  const paymentId = Math.trunc(Number(value.paymentId))
  if (!Number.isFinite(paymentId) || paymentId <= 0) return null

  const createdAtMs = Number(value.createdAtMs)
  const normalizedCreatedAtMs = Number.isFinite(createdAtMs) && createdAtMs > 0
    ? createdAtMs
    : Date.now()

  return {
    paymentId,
    providerPaymentId: typeof value.providerPaymentId === 'string' ? value.providerPaymentId : undefined,
    amountMinor: Number.isFinite(Number(value.amountMinor)) ? Number(value.amountMinor) : undefined,
    currency: typeof value.currency === 'string' ? value.currency : undefined,
    credits: Number.isFinite(Number(value.credits)) ? Number(value.credits) : undefined,
    packCode: typeof value.packCode === 'string' ? value.packCode : undefined,
    packTitle: typeof value.packTitle === 'string' ? value.packTitle : undefined,
    createdAtMs: normalizedCreatedAtMs
  }
}

export function readPaymentAnalyticsOutbox(): PaymentAnalyticsRecord[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(PAYMENT_ANALYTICS_OUTBOX_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const oldestAllowed = Date.now() - MAX_RECORD_AGE_MS
    return parsed
      .map(normalizeRecord)
      .filter((record): record is PaymentAnalyticsRecord =>
        record !== null && record.createdAtMs >= oldestAllowed
      )
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
      .slice(0, MAX_RECORDS)
  } catch {
    return []
  }
}

function writePaymentAnalyticsOutbox(records: PaymentAnalyticsRecord[]) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      PAYMENT_ANALYTICS_OUTBOX_KEY,
      JSON.stringify(records.slice(0, MAX_RECORDS))
    )
    window.dispatchEvent(new Event(PAYMENT_ANALYTICS_OUTBOX_UPDATED_EVENT))
  } catch {
    // Analytics must never interrupt checkout.
  }
}

export function enqueuePaymentAnalytics(record: Omit<PaymentAnalyticsRecord, 'createdAtMs'> & { createdAtMs?: number }) {
  const normalized = normalizeRecord({
    ...record,
    createdAtMs: record.createdAtMs ?? Date.now()
  })
  if (!normalized) return

  const current = readPaymentAnalyticsOutbox()
  const previous = current.find((item) => item.paymentId === normalized.paymentId)
  const merged = previous ? { ...previous, ...normalized } : normalized
  const next = [
    merged,
    ...current.filter((item) => item.paymentId !== normalized.paymentId)
  ].sort((a, b) => b.createdAtMs - a.createdAtMs)
  writePaymentAnalyticsOutbox(next)
}

export function removePaymentAnalytics(paymentId: number) {
  const normalizedPaymentId = Math.trunc(Number(paymentId))
  if (!Number.isFinite(normalizedPaymentId) || normalizedPaymentId <= 0) return
  const current = readPaymentAnalyticsOutbox()
  const next = current.filter((item) => item.paymentId !== normalizedPaymentId)
  if (next.length === current.length) return
  writePaymentAnalyticsOutbox(next)
}
